
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Terrace } from '@/types/terrace';
import { TerraceDataTransformer } from '@/lib/services/TerraceDataTransformer';
import { toast } from 'sonner';

export const useTerraceData = () => {
  const [terraces, setTerraces] = useState<Terrace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTerraces = async () => {
    setLoading(true);
    try {
      // First get terraces
      const { data: terraceData, error: terraceError } = await supabase
        .from('terraces')
        .select('*')
        .order('created_at', { ascending: false });

      if (terraceError) {
        console.error("Error fetching terraces:", terraceError);
        setError("Failed to load terraces");
        toast.error("Failed to load terraces");
        return;
      }

      if (terraceData) {
        // Get unique user IDs from terraces that have submitted_by
        const userIds = [...new Set(terraceData
          .map(t => t.submitted_by)
          .filter(id => id && id.length === 36))] // Filter for valid UUIDs
          .filter(Boolean);

        // Fetch user names for these IDs if any exist
        let userNamesMap: Record<string, string> = {};
        if (userIds.length > 0) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('id, name')
            .in('id', userIds);

          if (profileData) {
            userNamesMap = profileData.reduce((acc, profile) => {
              acc[profile.id] = profile.name || 'Unknown';
              return acc;
            }, {} as Record<string, string>);
          }
        }

        const transformedData: Terrace[] = terraceData.map(terrace => {
          // Transform using centralized transformer
          const transformedTerrace = TerraceDataTransformer.transformDatabaseToTerrace(terrace);
          
          // Get the display name for submitted_by
          const submittedByDisplay = terrace.submitted_by && userNamesMap[terrace.submitted_by] 
            ? userNamesMap[terrace.submitted_by]
            : terrace.submitted_by || 'Unknown';

          return {
            ...transformedTerrace,
            submitted_by: submittedByDisplay,
          };
        });
        
        setTerraces(transformedData);
        setError(null);
      }
    } catch (err) {
      console.error("Error fetching terraces:", err);
      setError("Failed to load terraces");
      toast.error("Failed to load terraces");
    } finally {
      setLoading(false);
    }
  };

  return {
    terraces,
    loading,
    error,
    setTerraces,
    fetchTerraces
  };
};
